<template>
  <div class="gauge-chart" ref="gaugeContainer">
    <canvas ref="gaugeCanvas" />    
  </div>
</template>

<style scoped>
.gauge-chart {
  width: 200px;
  height: 140px;
}
.gauge-chart > canvas {
  width: 100%;
  height: 100%;
}
</style>

<script>
import MathsUtils from '@/utils/mathsUtils'
import * as THREE from 'three'

export default {
  name: 'GaugeChart',
  mounted() {

    this.$nextTick(() => {
      this.renderGauge(this.$refs.gaugeCanvas, this.$refs.gaugeContainer)
    })
  },
  methods: {
    renderGauge(canvasElement, containerElement) {
      var ctx = canvasElement.getContext('2d')
      var greenColour = '#49DE80'
      var yellowColour = '#FDE046'
      var redColour = '#EF4444'
      var bgColour = '#D1D4DA'
      var needleColour = '#374150'
      var needleOutsideColour = '#D1D4DA'
      var pinColour = '#FFFFFE'
      var tickColour = bgColour
      var textColour = needleColour

      // https://github.com/mattrothenberg/use-gauge/tree/main

      canvasElement.width = containerElement.clientWidth
      canvasElement.height = containerElement.clientHeight

      // TODO: Ticks
      // TODO: Draw needle with transform instead of lots of maths

      var smTickSize = 5
      var lgTickSize = 10
      var padding = 10
      var gaugeSize = 35
      var outerRadius = Math.round(containerElement.clientWidth/2 - padding*2)
      var innerRadius = outerRadius - gaugeSize
      var offsetAngleRads = 0.5
      var needleStartWidth = 20
      var needleEndWidth = 4
      var needleLength = outerRadius + 12
      var pivotRadius = 16
      var pinRadius = 3
      var tickSpacing = 3

      var largeTickCount = 5
      var smallTickCount = 5    // Small ticks between each large tick
      var textHeight = 18

      var rangeStartAngle = (Math.PI + offsetAngleRads)
      var rangeEndAngle = (Math.PI*2 - offsetAngleRads)
      var rangeRads = rangeEndAngle - rangeStartAngle
      var greenEndAngle = rangeStartAngle + rangeRads * 0.50
      var yellowEndAngle = rangeStartAngle + rangeRads * 0.7

      var largeTickAngle = rangeRads / (largeTickCount-1)
      var smallTickAngle = largeTickAngle / smallTickCount

      var midX = canvasElement.width / 2.0
      var bottomY = canvasElement.height - textHeight

      var needleAngle = -Math.PI/2

      // temp
      var tempPercent = 0.2 + Math.abs(Math.random())
      needleAngle = rangeStartAngle + ((tempPercent / 1.2) * rangeRads)

      var highlightColour = greenColour
      if(needleAngle-rangeStartAngle > rangeRads * 0.50)
        highlightColour = yellowColour
      if(needleAngle-rangeStartAngle > rangeRads * 0.7)
        highlightColour = redColour

      var midPoint = new THREE.Vector2(midX, bottomY - padding - needleStartWidth)

      var startDir = MathsUtils.generate2DDirectionVector(rangeStartAngle)
      var endDir = MathsUtils.generate2DDirectionVector(rangeEndAngle)

      var needleDir = MathsUtils.generate2DDirectionVector(needleAngle)
      var needleRightDir = new THREE.Vector2(-needleDir.y, needleDir.x)

      var startOuter = midPoint.clone().addScaledVector(startDir, outerRadius)
      var endGreenInner = midPoint.clone().addScaledVector(MathsUtils.generate2DDirectionVector(greenEndAngle), innerRadius)
      var endYellowInner = midPoint.clone().addScaledVector(MathsUtils.generate2DDirectionVector(yellowEndAngle), innerRadius)
      var endInner = midPoint.clone().addScaledVector(endDir, innerRadius)

      var needleInner = midPoint.clone().addScaledVector(MathsUtils.generate2DDirectionVector(needleAngle), innerRadius)

      // All stages displayed
      /*
      // Green
      ctx.fillStyle = greenColour
      ctx.beginPath()
      ctx.arc(midPoint.x, midPoint.y, outerRadius, rangeStartAngle, greenEndAngle, false)
      ctx.lineTo(endGreenInner.x, endGreenInner.y)
      ctx.arc(midPoint.x, midPoint.y, innerRadius, greenEndAngle, rangeStartAngle, true)
      ctx.lineTo(startOuter.x, startOuter.y)
      ctx.fill()

      // Yellow
      ctx.fillStyle = yellowColour
      ctx.beginPath()
      ctx.arc(midPoint.x, midPoint.y, outerRadius, greenEndAngle, yellowEndAngle, false)
      ctx.lineTo(endYellowInner.x, endYellowInner.y)
      ctx.arc(midPoint.x, midPoint.y, innerRadius, yellowEndAngle, greenEndAngle, true)
      ctx.lineTo(endGreenInner.x, endGreenInner.y)
      ctx.fill()

      // Red
      ctx.fillStyle = redColour
      ctx.beginPath()
      ctx.arc(midPoint.x, midPoint.y, outerRadius, yellowEndAngle, rangeEndAngle, false)
      ctx.lineTo(endInner.x, endInner.y)
      ctx.arc(midPoint.x, midPoint.y, innerRadius, rangeEndAngle, yellowEndAngle, true)
      ctx.lineTo(endYellowInner.x, endYellowInner.y)
      ctx.fill()
      */

      // BG
      ctx.fillStyle = bgColour
      ctx.beginPath()
      ctx.arc(midPoint.x, midPoint.y, outerRadius, rangeStartAngle, rangeEndAngle, false)
      ctx.lineTo(endInner.x, endInner.y)
      ctx.arc(midPoint.x, midPoint.y, innerRadius, rangeEndAngle, rangeStartAngle, true)
      ctx.lineTo(startOuter.x, startOuter.y)
      ctx.fill()

      // current
      ctx.fillStyle = highlightColour
      ctx.beginPath()
      ctx.arc(midPoint.x, midPoint.y, outerRadius, rangeStartAngle, needleAngle, false)
      ctx.lineTo(needleInner.x, needleInner.y)
      ctx.arc(midPoint.x, midPoint.y, innerRadius, needleAngle, rangeStartAngle, true)
      ctx.lineTo(startOuter.x, startOuter.y)
      ctx.fill()

      // Ticks
      ctx.strokeStyle = tickColour
      ctx.beginPath()
      for(let i=0; i<largeTickCount; i++) {
        let lgTickAngle = rangeStartAngle + largeTickAngle * i
        let lgTickDir = MathsUtils.generate2DDirectionVector(rangeStartAngle + largeTickAngle * i)
        var lgTickStart = midPoint.clone().addScaledVector(lgTickDir, outerRadius + tickSpacing)
        var lgTickEnd = lgTickStart.clone().addScaledVector(lgTickDir, lgTickSize)
        
        ctx.moveTo(lgTickStart.x, lgTickStart.y)
        ctx.lineTo(lgTickEnd.x, lgTickEnd.y)

        if(i < largeTickCount-1) {
          for(let j=0; j<smallTickCount; j++) {
            let smTickDir = MathsUtils.generate2DDirectionVector(lgTickAngle + smallTickAngle * j)
            var smTickStart = midPoint.clone().addScaledVector(smTickDir, outerRadius + tickSpacing)
            var smTickEnd = smTickStart.clone().addScaledVector(smTickDir, smTickSize)

            ctx.moveTo(smTickStart.x, smTickStart.y)
            ctx.lineTo(smTickEnd.x, smTickEnd.y)
          }
        }
      }
      ctx.stroke()

      // Needle BG
      ctx.beginPath()
      ctx.ellipse(midPoint.x, midPoint.y, pivotRadius, pivotRadius, 0, 0, Math.PI*2)
      ctx.fillStyle = needleOutsideColour
      ctx.fill()

      // Needle
      ctx.beginPath()
      var needleStartLeft = midPoint.clone().addScaledVector(needleRightDir, -needleStartWidth / 2)
      var needleStartRight = midPoint.clone().addScaledVector(needleRightDir, needleStartWidth / 2)
      var needleEndPoint = midPoint.clone().addScaledVector(needleDir, needleLength) 
      var needleEndLeft = needleEndPoint.clone().addScaledVector(needleRightDir, -needleEndWidth / 2)
      var needleEndRight = needleEndPoint.clone().addScaledVector(needleRightDir, needleEndWidth / 2)
      ctx.arc(midPoint.x, midPoint.y, needleStartWidth/2, needleAngle - Math.PI/2, needleAngle + Math.PI/2, true)
      ctx.lineTo(needleEndRight.x, needleEndRight.y)
      ctx.arc(needleEndPoint.x, needleEndPoint.y, needleEndWidth/2, needleAngle + Math.PI/2, needleAngle - Math.PI/2, true)
      ctx.lineTo(needleStartLeft.x, needleStartLeft.y)

      ctx.fillStyle = needleColour
      ctx.fill()

      // Needle Pin
      ctx.beginPath()
      ctx.ellipse(midPoint.x, midPoint.y, pinRadius, pinRadius, 0, 0, Math.PI*2)
      ctx.fillStyle = pinColour
      ctx.fill()

      // Text
      ctx.fillStyle = textColour
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.font = `600 15px sans-serif`
      ctx.fillText(`${(tempPercent * 100).toFixed(0)}%`, midX, canvasElement.height - 8)


    }
  }
}
</script>